// frontend/src/contexts/SocketContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import axios from '../config/axios';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
//const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SocketContext = createContext();

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket debe ser usado dentro de un SocketProvider');
    }
    return context;
};

export const SocketProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        if (!isAuthenticated || !user) {
            console.log('Usuario no autenticado, no se intentará conexión socket');
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            console.log('No se encontró token en localStorage');
            return;
        }

        console.log('Iniciando conexión socket en:', SOCKET_URL);

        const socketInstance = io(SOCKET_URL, {
            path: '/socket.io/',
            auth: {
                token: `Bearer ${token}`
            },
            transports: ['websocket'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5
        });

        socketInstance.on('connect', () => {
            console.log('Conexión socket establecida exitosamente');
            setIsConnected(true);
            fetchNotifications();
        });

        socketInstance.on('disconnect', () => {
            console.log('Socket desconectado');
            setIsConnected(false);
        });

        socketInstance.on('connect_error', (error) => {
            console.error('Error de conexión socket:', error);
            setIsConnected(false);
        });

        socketInstance.on('notification', (notification) => {
            console.log('Nueva notificación recibida:', notification);
            handleNewNotification(notification);
        });

        setSocket(socketInstance);

        return () => {
            console.log('Limpiando conexión socket...');
            if (socketInstance) {
                if (socketInstance.readyState === "open")
                {
                    socketInstance.disconnect();
                }
            }
        };
    }, [isAuthenticated, user]);

    const fetchNotifications = async () => {
        try {
            console.log('Obteniendo notificaciones existentes...');
            const response = await axios.get('/notifications');
            if (response.data.success) {
                console.log('Notificaciones obtenidas:', response.data.notifications);
                setNotifications(response.data.notifications);
                setUnreadCount(response.data.notifications.filter(n => !n.read).length);
            }
        } catch (error) {
            console.error('Error al obtener notificaciones:', error);
        }
    };

    const handleNewNotification = (notification) => {
        console.log('Procesando nueva notificación:', notification);
        setNotifications(prev => {
            if (prev.some(n => n.id === notification.id)) {
                console.log('Notificación duplicada, ignorando');
                return prev;
            }
            console.log('Agregando nueva notificación al estado');
            return [notification, ...prev];
        });
        setUnreadCount(prev => prev + 1);
    };

    const markAsRead = async (notificationId) => {
        try {
            await axios.post(`/notifications/${notificationId}/read`);
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId ? { ...n, read: true } : n
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            await axios.delete(`/notifications/${notificationId}`);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            setUnreadCount(prev =>
                prev - (notifications.find(n => n.id === notificationId)?.read ? 0 : 1)
            );
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    const clearAllNotifications = async () => {
        try {
            await axios.delete('/notifications');
            setNotifications([]);
            setUnreadCount(0);
        } catch (error) {
            console.error('Error clearing notifications:', error);
        }
    };

    // Nueva función para marcar todas las notificaciones de un torneo como leídas y eliminarlas
    const markTournamentNotificationsAsRead = async (tournamentId) => {
        try {
            // Filtrar todas las notificaciones del torneo
            const tournamentNotifications = notifications.filter(
                n => n.tournamentId === tournamentId
            );
            
            if (tournamentNotifications.length === 0) return;
            
            console.log(`Marcando ${tournamentNotifications.length} notificaciones del torneo ${tournamentId} como leídas`);
            
            // Marcar todas como leídas y eliminarlas
            await Promise.all(
                tournamentNotifications.map(async (notification) => {
                    await axios.post(`/notifications/${notification.id}/read`);
                    await axios.delete(`/notifications/${notification.id}`);
                })
            );
            
            // Actualizar estado local - eliminar todas las notificaciones del torneo
            setNotifications(prev => 
                prev.filter(n => n.tournamentId !== tournamentId)
            );
            
            // Actualizar contador de no leídas
            const unreadFromTournament = tournamentNotifications.filter(n => !n.read).length;
            setUnreadCount(prev => Math.max(0, prev - unreadFromTournament));
            
            console.log(`✅ Eliminadas ${tournamentNotifications.length} notificaciones del torneo ${tournamentId}`);
            
        } catch (error) {
            console.error('Error marking tournament notifications as read:', error);
        }
    };

    return (
        <SocketContext.Provider value={{
            socket,
            notifications,
            unreadCount,
            isConnected,
            markAsRead,
            deleteNotification,
            clearAllNotifications,
            markTournamentNotificationsAsRead
        }}>
            {children}
        </SocketContext.Provider>
    );
};